import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Container state that lives in the URL.
 *
 * Tabs, detail panels and collapsible sections used to be local `useState`, which made everything
 * inside them unreachable by a link. Expressing them as query parameters means a deep link only
 * has to build a URL, and any of these states can be hand-written or bookmarked.
 */

export const TAB_PARAM = 'tab';
export const PANEL_PARAM = 'panel';
export const PANEL_TAB_PARAM = 'panelTab';
export const INSTANCE_PARAM = 'instance';
export const EXPAND_PARAM = 'expand';
const FOCUS_PARAM = 'focus';

export type UrlHistoryMode = 'push' | 'replace';

/**
 * Rewrite the query string.
 *
 * Reads from `window.location` rather than the captured location so callers fired from stale
 * closures still merge into the current URL.
 */
const useQueryWriter = () => {
    const history = useHistory();
    return React.useCallback(
        (
            mutate: (params: URLSearchParams) => void,
            options?: { keepFocus?: boolean; historyMode?: UrlHistoryMode }
        ) => {
            const params = new URLSearchParams(window.location.search);
            mutate(params);
            // Moving to a different tab or closing a panel leaves any focused setting behind.
            if (!options?.keepFocus) params.delete(FOCUS_PARAM);
            const search = params.toString();
            const next = { pathname: window.location.pathname, search: search ? `?${search}` : '' };
            if (options?.historyMode === 'push') history.push(next);
            else history.replace(next);
        },
        [history]
    );
};

export interface IUrlTabOptions {
    param?: string;
    /**
     * Whether changing tab adds a history entry. Defaults to 'replace', which matches how tabs
     * behaved when they were local state - Back leaves the screen rather than stepping back through
     * tabs. Pass 'push' on screens that already pushed, so their Back behavior is unchanged.
     */
    historyMode?: UrlHistoryMode;
}

/** Two-way bind a Fluent `Pivot` to `?tab=`. */
export const useUrlTabState = (
    defaultKey: string,
    options?: IUrlTabOptions
): [string, (key?: string) => void] => {
    const location = useLocation();
    const write = useQueryWriter();
    const param = options?.param ?? TAB_PARAM;
    const historyMode = options?.historyMode;
    const selected = new URLSearchParams(location.search).get(param) ?? defaultKey;

    const setSelected = React.useCallback(
        (key?: string) => write((params) => params.set(param, key ?? defaultKey), { historyMode }),
        [write, param, defaultKey, historyMode]
    );

    return [selected, setSelected];
};

export interface IUrlPanelState {
    isOpen: boolean;
    /** Id of the record the panel is showing, from `?instance=`. */
    instanceId?: string;
    /** Selected tab within the panel, from `?panelTab=`. */
    panelTab?: string;
    open: (instanceId?: string, panelTab?: string) => void;
    close: () => void;
    setPanelTab: (panelTab?: string) => void;
}

/**
 * Bind a detail Panel/Modal to `?panel=<key>&instance=<id>&panelTab=<tab>`.
 *
 * A screen with several panels calls this once per panel key; only the one named in the URL
 * reports `isOpen`.
 */
export const useUrlPanelState = (panelKey: string): IUrlPanelState => {
    const location = useLocation();
    const write = useQueryWriter();
    const params = new URLSearchParams(location.search);
    const isOpen = params.get(PANEL_PARAM) === panelKey;

    const open = React.useCallback(
        (instanceId?: string, panelTab?: string) =>
            write(
                (next) => {
                    next.set(PANEL_PARAM, panelKey);
                    if (instanceId) next.set(INSTANCE_PARAM, instanceId);
                    else next.delete(INSTANCE_PARAM);
                    if (panelTab) next.set(PANEL_TAB_PARAM, panelTab);
                    else next.delete(PANEL_TAB_PARAM);
                },
                // Opening the panel is often how a focused setting becomes visible.
                { keepFocus: true }
            ),
        [write, panelKey]
    );

    const close = React.useCallback(
        () =>
            write((next) => {
                next.delete(PANEL_PARAM);
                next.delete(INSTANCE_PARAM);
                next.delete(PANEL_TAB_PARAM);
            }),
        [write]
    );

    const setPanelTab = React.useCallback(
        (panelTab?: string) =>
            write((next) => {
                if (panelTab) next.set(PANEL_TAB_PARAM, panelTab);
                else next.delete(PANEL_TAB_PARAM);
            }),
        [write]
    );

    return {
        isOpen,
        instanceId: isOpen ? params.get(INSTANCE_PARAM) ?? undefined : undefined,
        panelTab: isOpen ? params.get(PANEL_TAB_PARAM) ?? undefined : undefined,
        open,
        close,
        setPanelTab,
    };
};

export interface IUrlExpandState {
    isExpanded: (key: string) => boolean;
    expand: (key: string) => void;
    collapse: (key: string) => void;
    toggle: (key: string) => void;
}

/**
 * Bind collapsible sections to `?expand=a,b`, so a setting inside a collapsed accordion can be
 * revealed by a link.
 */
export const useUrlExpandState = (param: string = EXPAND_PARAM): IUrlExpandState => {
    const location = useLocation();
    const write = useQueryWriter();
    const expanded = React.useMemo(
        () => (new URLSearchParams(location.search).get(param) ?? '').split(',').filter(Boolean),
        [location.search, param]
    );

    const writeKeys = React.useCallback(
        (keys: string[]) =>
            write(
                (next) => {
                    if (keys.length) next.set(param, keys.join(','));
                    else next.delete(param);
                },
                { keepFocus: true }
            ),
        [write, param]
    );

    return {
        isExpanded: (key: string) => expanded.includes(key),
        expand: (key: string) => {
            if (!expanded.includes(key)) writeKeys([...expanded, key]);
        },
        collapse: (key: string) => writeKeys(expanded.filter((k) => k !== key)),
        toggle: (key: string) =>
            expanded.includes(key) ? writeKeys(expanded.filter((k) => k !== key)) : writeKeys([...expanded, key]),
    };
};
